import ButtonGroup from "@visa/vds/button-group";
import ButtonText from "@visa/vds/button-text";
import Col from "@visa/vds/col";
import Grid from "@visa/vds/grid";
import Row from "@visa/vds/row";
import { AuthContext } from "context/AuthContext";
import { cloneDeep } from "lodash";
import React, { useEffect, useState } from "react";
import { trackPromise, usePromiseTracker } from "react-promise-tracker";
import { useHistory, useLocation } from "react-router-dom";
import { AlertMessages } from "_constants/AlertMessages";
import { FileHelper } from "_helpers/FileHelper";
import MCSHeader from "../../components/MCSHeader";
import MCSLoading from "../../components/MCSLoading";
import { GlobalAlertContext } from "../../context/GlobalAlertContext";
import { DafVmidApi } from "../../services/daf.vmid.service";
import ToggleFlagOrParam from "./Components/ToggleFlagOrParam";
import VmidRecordDialog from "./Components/VmidRecordDialog";
import VmidTable from "./Components/VmidTable";

const VmidList = () => {
  const location = useLocation();
  const history = useHistory();
  const [error, setError] = useState(null);
  const { promiseInProgress } = usePromiseTracker();
  const [tableData, setTableData] = useState(null);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("lastUpdatedOn");
  const [desc, setDesc] = useState(true);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [showFlag, setShowFlag] = useState(true);
  const [showParam, setShowParam] = useState(false);
  const [searchVal, setSearchVal] = useState(null);
  const [vmidRecord, setVmidRecord] = useState(null);
  const [dialog, setDialog] = useState({ open: false });

  const pageSize = 10;

  function handleSearchChange(newSearchVal) {
    setPage(1);
    fetchData(1, sortBy, desc, newSearchVal);
  }

  const handleSortChange = async (sortBy = null, desc = false) => {
    fetchData(page, sortBy, desc, searchVal);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchData(newPage, sortBy, desc, searchVal);
  };

  const fetchData = async (page, sortBy, desc, searchVal) => {
    let skipCount = (page - 1) * pageSize;
    trackPromise(
      DafVmidApi.getVmidList(skipCount, sortBy, desc, searchVal)
        .then((result) => {
          setTableData(result.data.items);
          setTotalCount(result.data.totalCount);
          setTotalPages(Math.ceil(result.data.totalCount / pageSize));
        })
        .catch((error) => setError(error))
    );
  };

  const setVmidRecordOnOpenDialog = (vmid) => {
    let temp = cloneDeep(vmid);
    temp = {
      ...temp,
      flagOne: vmid.flagOne.toString(),
      flagTwo: vmid.flagTwo.toString(),
      flagThree: vmid.flagThree.toString(),
      flagFour: vmid.flagFour.toString()
    };
    setVmidRecord(temp);
  };

  const handleClickAction = (e) => {
    let id = Number(e.currentTarget.dataset.id);
    const vmid = tableData.filter((i) => {
      return i.id === id;
    });
    if (vmid && vmid.length > 0) {
      setVmidRecordOnOpenDialog(vmid[0]);
      setDialog({ open: true });
    }
  };

  const handleClickParametersDownload = (alertObj) => {
    DafVmidApi.downloadVmidParametersFile()
      .then((response) => {
        FileHelper.DownloadFile(response);
      })
      .catch((error) => {
        alertObj.setErrorMessage(AlertMessages.UnexpectedError);
      });
  };

  const handleClickParticipationDownload = (alertObj) => {
    DafVmidApi.downloadVmidParticipationFile()
      .then((response) => {
        FileHelper.DownloadFile(response);
      })
      .catch((error) => {
        alertObj.setErrorMessage(AlertMessages.UnexpectedError);
      });
  };

  const handleClickSave = (alertObj, updateDto) => {
    setDialog({ open: false });
    trackPromise(
      DafVmidApi.saveVmid(updateDto)
        .then((res) => {
          alertObj.setSuccessMessage(AlertMessages.SaveSuccess);
          fetchData(page, sortBy, desc, searchVal);
        })
        .catch((error) => {
          alertObj.setErrorMessage(error);
        })
    );
  };

  useEffect(() => {
    fetchData(1, sortBy, desc, searchVal);
  }, []);

  if (error) {
    return <div>Error: {error.message}</div>;
  } else {
    return (
      <AuthContext.Consumer>
        {(data) => (
          <GlobalAlertContext.Consumer>
            {(alertObj) => (
              <>
                <Grid>
                  <MCSHeader header="View VMIDs" />
                  {(data.user.isSuperAdmin ||
                    data.user.isVmidParticipation) && (
                    <>
                      <VmidRecordDialog
                        alertObj={alertObj}
                        dialog={dialog}
                        setDialog={setDialog}
                        vmidRecord={vmidRecord}
                        setVmidRecord={setVmidRecord}
                        handleClickSave={handleClickSave}
                      />
                      <Row>
                        <Col>
                          <ToggleFlagOrParam
                            setShowFlag={setShowFlag}
                            setShowParam={setShowParam}
                          />
                        </Col>
                        <Col>
                          <ButtonGroup direction="right">
                            <ButtonText
                              onClick={handleClickParticipationDownload}
                            >
                              DOWNLOAD VMID PARTICIPATION FILE
                            </ButtonText>
                            <ButtonText onClick={handleClickParametersDownload}>
                              DOWNLOAD VMID PARAMETERS FILE
                            </ButtonText>
                          </ButtonGroup>
                        </Col>
                      </Row>
                    </>
                  )}
                  {promiseInProgress && <MCSLoading />}
                  {!promiseInProgress && tableData && (
                    <>
                      <Row>
                        <Col>
                          <VmidTable
                            user={data.user}
                            showFlag={
                              data.user.isSuperAdmin ||
                              data.user.isVmidParticipation
                                ? showFlag
                                : false
                            }
                            showParam={
                              data.user.isSuperAdmin ||
                              data.user.isVmidParticipation
                                ? showParam
                                : true
                            }
                            selectedPage={page}
                            setSelectedPage={handlePageChange}
                            totalPages={totalPages}
                            ordered={sortBy}
                            setOrdered={setSortBy}
                            sortData={handleSortChange}
                            isDsc={desc}
                            setDsc={setDesc}
                            pageSize={pageSize}
                            totalCount={totalCount}
                            tableData={tableData}
                            handleClickAction={handleClickAction}
                            handleSearchChange={handleSearchChange}
                            searchVal={searchVal}
                            setSearchVal={setSearchVal}
                          />
                        </Col>
                      </Row>
                    </>
                  )}
                </Grid>
              </>
            )}
          </GlobalAlertContext.Consumer>
        )}
      </AuthContext.Consumer>
    );
  }
};
export default VmidList;
